import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/saas/AuthContext";
import { useI18n, usePageTitle } from "@/i18n";
import { LanguageSelector } from "@/components/LanguageSelector";
import { errMsg } from "@/lib/saasApi";

const PLAN_LABEL = { starter: "Starter", growth: "Growth", pro: "Pro" };

export default function Register() {
  const { register, user } = useAuth();
  const { t } = useI18n();
  usePageTitle("titles.register");
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const plan = params.get("plan");
  const next = params.get("next");
  const submitting = useRef(false); // guard: don't override this form's own navigation

  // Redirect visitors who were ALREADY logged in on entry (never a second account).
  // Skip while THIS form is registering, so its own destination isn't overridden.
  useEffect(() => {
    if (user && !submitting.current) nav(user.role === "OWNER_ADMIN" ? "/admin" : "/dashboard", { replace: true });
  }, [user, nav]);

  const [f, setF] = useState({ firstName: "", lastName: "", companyName: "", email: "", password: "", confirm: "" });
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const errs = [];
    if (f.password.length < 8) errs.push(t("auth.register.errShort"));
    if (f.password !== f.confirm) errs.push(t("auth.register.errMismatch"));
    if (!accept) errs.push(t("auth.register.errAccept"));
    if (errs.length) return setError(errs);
    setLoading(true);
    submitting.current = true;
    try {
      const data = await register({
        firstName: f.firstName, lastName: f.lastName, companyName: f.companyName,
        email: f.email, password: f.password, acceptTerms: accept, plan: plan || undefined,
      });
      const dest = data.pendingPlanId
        ? `/account/billing?plan=${data.pendingPlanId}`
        : (next && next.startsWith("/") ? next : "/dashboard");
      nav(dest, { replace: true });
    } catch (err) {
      submitting.current = false;
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-re-soft px-6 py-10" data-testid="register-page">
      <div className="w-full max-w-md rounded-3xl border border-re-line bg-white p-8 shadow-sm md:p-10">
        <div className="flex items-center justify-between"><Logo variant="light" emblemY={7} /><LanguageSelector /></div>
        <h1 className="mt-7 font-heading text-2xl font-bold text-re-charcoal">{t("auth.register.title")}</h1>
        <p className="mt-1 text-sm text-re-muted">{t("auth.register.sub")}</p>
        {plan && PLAN_LABEL[plan] && (
          <div className="mt-4 rounded-xl bg-re-red/5 px-4 py-2.5 text-sm text-re-charcoal" data-testid="register-plan-badge">
            {t("app.billing.currentPlan")}: <span className="font-semibold text-re-red">{PLAN_LABEL[plan]}</span>
          </div>
        )}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>{t("auth.register.firstName")}</Label><Input value={f.firstName} onChange={on("firstName")} data-testid="reg-first" className="border-re-line" /></div>
            <div className="space-y-2"><Label>{t("auth.register.lastName")}</Label><Input value={f.lastName} onChange={on("lastName")} data-testid="reg-last" className="border-re-line" /></div>
          </div>
          <div className="space-y-2"><Label>{t("auth.register.company")}</Label><Input value={f.companyName} onChange={on("companyName")} data-testid="reg-company" className="border-re-line" /></div>
          <div className="space-y-2"><Label>{t("auth.register.email")}</Label><Input type="email" value={f.email} onChange={on("email")} data-testid="reg-email" className="border-re-line" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>{t("auth.register.password")}</Label><Input type="password" value={f.password} onChange={on("password")} data-testid="reg-password" className="border-re-line" /></div>
            <div className="space-y-2"><Label>{t("auth.register.confirm")}</Label><Input type="password" value={f.confirm} onChange={on("confirm")} data-testid="reg-confirm" className="border-re-line" /></div>
          </div>
          <label className="flex items-start gap-2.5 text-sm text-re-muted">
            <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="reg-accept" className="mt-0.5" />
            <span>{t("auth.register.terms")}</span>
          </label>
          {error && (
            <div className="space-y-1 text-sm text-re-red" data-testid="register-error">
              {(Array.isArray(error) ? error : [error]).map((msg, i) => <p key={i}>{msg}</p>)}
            </div>
          )}
          <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-full bg-re-red py-6 text-base font-semibold text-white hover:bg-re-deepred">
            {loading ? t("auth.register.submitting") : t("auth.register.submit")}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-re-muted">
          {t("auth.register.hasAccount")}{" "}
          <Link to="/login" data-testid="register-to-login" className="font-semibold text-re-red hover:underline">{t("auth.register.login")}</Link>
        </p>
      </div>
    </div>
  );
}
